import { Portal } from '@design/components/portal';
import {
  Alignment,
  arrow,
  autoUpdate,
  flip,
  hide,
  Middleware,
  offset,
  Padding,
  Placement,
  safePolygon,
  shift,
  Side,
  Strategy,
  useClick,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import React, {
  cloneElement,
  createContext,
  CSSProperties,
  ReactElement,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './Tooltip.module.scss';

export const TooltipAncestorContext = createContext(false);

export enum TooltipTrigger {
  Click = 'click',
  Focus = 'focus',
  Hover = 'hover',
}

export interface TooltipProps {
  /** Forwarded to the trigger element via `cloneElement`. */
  ref?: Ref<HTMLElement>;
  /** Content rendered inside the tooltip bubble. */
  label: React.ReactNode;
  /** Floating-UI placement (e.g. `'top'`, `'bottom-end'`). Defaults to `'top'`. */
  placement?: Placement;
  /** The trigger element. Must be a single React element; tooltip props are merged onto it via `cloneElement`. */
  children: ReactElement<Record<string, unknown>>;
  /** Controlled open state. When omitted, the tooltip is uncontrolled and managed by `trigger` interactions. */
  isOpen?: boolean;
  /** When true, suppresses opening regardless of triggers and forces the bubble closed. */
  disabled?: boolean;
  /** Render the bubble into a portal at the document root. Use when ancestors clip overflow. */
  inPortal?: boolean;
  /** Allow the user to dismiss the tooltip (Escape, outside click). Defaults to `true`. */
  dismissible?: boolean;
  /** Apply Floating-UI's shared open/close delay group. Defaults to `true`. */
  hasDelay?: boolean;
  /** Floating-UI positioning strategy: `'absolute'` (default) or `'fixed'`. */
  strategy?: Strategy;
  /** Horizontal offset (px) added to the computed position. Defaults to `0`. */
  offsetX?: number;
  /** Vertical offset (px) added to the computed position. Defaults to `0`. */
  offsetY?: number;
  /** Called when the tooltip opens (any non-`disabled` open transition). */
  onOpen?: () => void;
  /** Called when the tooltip closes. */
  onClose?: () => void;
  /** Render the small pointing arrow against the trigger. Defaults to `true`. */
  hasArrow?: boolean;
  /** Which interactions open the tooltip. Single `TooltipTrigger` or an array. Defaults to `[Click, Focus, Hover]`. */
  trigger?: TooltipTrigger | TooltipTrigger[];
  /** Text shown next to a tap-to-close affordance when `isTouchDevice` is true. */
  closeLabel?: string;
  /** Mobile/touch mode: tapping the bubble closes it and `closeLabel` is rendered. Defaults to `false`. */
  isTouchDevice?: boolean;
  /** Extra class forwarded to the floating bubble. */
  className?: string;
  /** Padding (px) for Floating-UI's `hide` middleware that hides the bubble when the trigger is occluded. */
  hidePadding?: Padding;
  /** Use Floating-UI's `safePolygon` so the tooltip stays open while moving toward it. Hover trigger only. */
  withSafePolygon?: boolean;
  /** Apply the design-system max-width to the bubble. Set `false` for free-flowing content. Defaults to `true`. */
  hasMaxWidth?: boolean;
  /** Options forwarded to Floating-UI's `shift` middleware (e.g. boundary, padding). */
  shiftOptions?: Parameters<typeof shift>[0];
}

interface ArrowStyleParams {
  x?: number;
  y?: number;
  placement: Side;
  alignment?: Alignment;
}

const getArrowStyle = ({
  x,
  y,
  placement,
  alignment,
}: ArrowStyleParams): CSSProperties => {
  const getPositionValue = (
    side: 'top' | 'right' | 'bottom' | 'left',
    n?: number
  ): CSSProperties => {
    if (alignment === 'start') {
      return { [side]: 8 };
    }

    if (alignment === 'end') {
      const oppositeSide: Record<string, string> = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      };

      return { [oppositeSide[side]]: 8 };
    }

    return { [side]: n };
  };

  switch (placement) {
    case 'left':
      return { left: -4, ...getPositionValue('top', y) };
    case 'right':
      return { right: -4, ...getPositionValue('top', y) };
    case 'bottom':
      return { bottom: -4, ...getPositionValue('left', x) };
    case 'top':
    default:
      return { top: -4, ...getPositionValue('left', x) };
  }
};

/**
 * Floating tooltip bubble anchored to a single child trigger element.
 *
 * Built on `@floating-ui/react`. The trigger may be any React element —
 * `<Tooltip>` clones it and attaches the necessary refs and event
 * handlers automatically. Open/close behaviour is driven by `trigger`
 * (any combination of click, focus, and hover) or fully controlled via
 * `isOpen`.
 */
export const Tooltip = ({
  ref,
  isOpen,
  children,
  label,
  placement = 'top',
  disabled = false,
  inPortal = false,
  strategy = 'absolute',
  dismissible = true,
  hasDelay = true,
  offsetX = 0,
  offsetY = 0,
  onOpen,
  onClose,
  hasArrow = true,
  withSafePolygon,
  trigger = [TooltipTrigger.Click, TooltipTrigger.Focus, TooltipTrigger.Hover],
  closeLabel,
  isTouchDevice = false,
  className,
  hidePadding = 0,
  hasMaxWidth = true,
  shiftOptions,
}: TooltipProps) => {
  const hasTooltipAncestor = useContext(TooltipAncestorContext);
  const arrowRef = useRef(null);
  const [open, setOpen] = useState(isOpen);

  const handlers = Array.isArray(trigger) ? trigger : [trigger];

  const canOpen = !disabled && !hasTooltipAncestor;

  const setOpenTooltip = useCallback(
    (nextValue: boolean) => {
      if (canOpen) {
        setOpen(nextValue);

        if (nextValue) {
          onOpen?.();
        } else {
          onClose?.();
        }
      } else {
        setOpen(false);
      }
    },
    [canOpen, onOpen]
  );

  const { x, y, middlewareData, context, refs } = useFloating({
    placement,
    open,
    onOpenChange: setOpenTooltip,
    strategy,
    middleware: [
      offset(5),
      flip(),
      shift({ ...shiftOptions, padding: 5 }),
      hasArrow ? arrow({ element: arrowRef }) : undefined,
      hide({
        padding: hidePadding,
      }),
    ].filter((m): m is Middleware => Boolean(m)),
    whileElementsMounted: autoUpdate,
  });

  const optionalUseHoverParams = withSafePolygon
    ? { handleClose: safePolygon() }
    : {};

  const { delay } = useDelayGroup(context, { enabled: hasDelay });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: handlers.includes(TooltipTrigger.Hover),
      delay,
      ...optionalUseHoverParams,
    }),
    useFocus(context, {
      enabled: handlers.includes(TooltipTrigger.Focus),
    }),
    useClick(context, {
      enabled: handlers.includes(TooltipTrigger.Click),
      ignoreMouse: handlers.includes(TooltipTrigger.Hover),
    }),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context, { enabled: dismissible }),
  ]);

  const refCallback = (node: HTMLElement | null) => {
    refs.setReference(node);

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const coords = middlewareData.arrow;
  const referenceHidden = middlewareData?.hide?.referenceHidden;

  const arrowPosition = context.placement || placement;

  const oppositeSides: Record<string, Side> = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  const [sidePart, alignPart] = arrowPosition.split('-');
  const staticSide = oppositeSides[sidePart] ?? 'bottom';

  const alignment: Alignment | undefined =
    alignPart === 'start' || alignPart === 'end' ? alignPart : undefined;

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    return () => setOpen(false);
  }, []);

  const tooltip = open && (
    <TooltipAncestorContext.Provider value>
      <div
        ref={refs.setFloating}
        onClick={() => isTouchDevice && setOpen(false)}
        className={clsx(
          styles.tooltipBubble,
          { [styles.tooltipBubbleNoMaxWidth]: !hasMaxWidth },
          className
        )}
        {...getFloatingProps({
          style: {
            visibility: referenceHidden ? 'hidden' : 'visible',
            position: strategy,
            top: (y ?? 0) + offsetY,
            left: (x ?? 0) + offsetX,
            zIndex: 'var(--z-tooltip)',
          },
        })}
        id="tooltip-content"
        data-testid="tooltip-content"
        data-tooltip-bubble=""
      >
        {label}
        {isTouchDevice && (
          <span
            className={styles.mobileTapClose}
            data-testid="close-label"
            data-tooltip-close=""
          >
            {closeLabel}
          </span>
        )}
        {hasArrow && (
          <div
            className={styles.arrowIcon}
            data-testid="tooltip-arrow"
            data-tooltip-arrow=""
            ref={arrowRef}
            style={getArrowStyle({
              x: coords?.x,
              y: coords?.y,
              placement: staticSide,
              alignment,
            })}
          />
        )}
      </div>
    </TooltipAncestorContext.Provider>
  );

  return (
    <>
      {cloneElement(children, {
        ref: refCallback,
        ...getReferenceProps(children.props),
      })}
      {inPortal ? <Portal>{tooltip}</Portal> : tooltip}
    </>
  );
};
